'use client';

import { faAngleDown, faSignOut, faTableColumns, faUser, faCreditCard } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar } from '@pickleballinc/react-ui';
import { useRouter } from 'next/navigation';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import CustomLink from '../ui/custom-link';

import getCDNURL from './utils/getCDNURL';
import { getDropdownUserMenuItems } from './utils/nav.config';
import { DropdownUserMenuItem, User } from './utils/types';

interface UserProfileItemProps {
	item: DropdownUserMenuItem;
	children: React.ReactNode;
	setOpen: Dispatch<SetStateAction<boolean>>;
	onClick: () => void;
}

function UserProfileItem({ item, children, onClick, setOpen }: UserProfileItemProps) {
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				onClick();
			}
		},
		[onClick]
	);

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center cursor-pointer hover:bg-gray-100 h-full rounded p-2">
				{item.label === 'Logout' ? (
					<div
						onClick={onClick}
						className="flex items-center w-full h-full gap-2"
						role="button"
						tabIndex={0}
						aria-label={item.label}
						onKeyDown={handleKeyDown}
						data-testid={item.testId}
						style={{
							fontFamily:
								"-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'"
						}}
					>
						{children}
						{item.label}
					</div>
				) : (
					<CustomLink
						onClick={() => setOpen(false)}
						className="flex items-center w-full h-full gap-2"
						href={item.src}
						data-testid={item.testId}
						style={{
							fontFamily:
								"-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'"
						}}
					>
						{children}
						{item.label}
					</CustomLink>
				)}
			</div>
			{item.border ? <div className="ml-2 mb-2 border-b border-gray-200" /> : null}
		</div>
	);
}

interface UserProfileProps {
	cdnUrl: string;
	pickleballHref: string;
	tournamentHref: string;
	leaguesHref: string;
	clubsHref: string;
	teamLeaguesHref: string;
	ssoLink: string;
	user: User;
	canAccessManage: boolean;
	isLocalhost: boolean;
	pathname: string | null;
	isPickleball: boolean;
}

export const UserProfile = ({
	cdnUrl,
	pickleballHref,
	tournamentHref,
	leaguesHref,
	clubsHref,
	teamLeaguesHref,
	ssoLink,
	user,
	canAccessManage,
	isLocalhost,
	pathname,
	isPickleball
}: UserProfileProps) => {
	const router = useRouter();
	const [open, setOpen] = useState(false);
	const [baseURL, setBaseURL] = useState('');
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setBaseURL(window.location.href);
		if (pathname?.startsWith('/players/')) {
			setBaseURL(`${window.location.origin}/players`);
		}
	}, []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target;
			if (dropdownRef.current && target instanceof Node && !dropdownRef.current.contains(target)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const profileImgArray = user && user.images ? user.images.filter((image) => image.type === 'profileImg') : [];
	const profileImgObj = profileImgArray[0];

	// const myPickleballPath = `${profilePath}/my-pickleball`;
	const profilePath = `${isLocalhost && isPickleball ? `http://${pickleballHref}` : `https://${pickleballHref}`}/players/${user.slug || user.uuid}`;
	const paymentsPath = `${profilePath}/profile/payments`;
	const managePath = `https://manage.${pickleballHref}`;
	const myTournamentsPath = `${tournamentHref}/my-tournaments`;
	const myLeaguesPath = `${leaguesHref}/leagues/my-leagues`;
	const myClubsPath = `${clubsHref}/my-clubs`;
	const myTeamLeaguesPath = `${teamLeaguesHref}/my-team-leagues`;
	const logoutPath = isLocalhost ? `/logout` : `${ssoLink}/v3/revoke?type=logout&continue=${encodeURIComponent(baseURL)}`;

	const dropdownMenuItems: DropdownUserMenuItem[] = getDropdownUserMenuItems({
		profilePath,
		paymentsPath,
		managePath,
		myClubsPath,
		myLeaguesPath,
		myTournamentsPath,
		myTeamLeaguesPath,
		logoutPath,
		manageAccess: canAccessManage
	});

	const isProPlayer = +user.proLevelId !== 4 && +user.proLevelId !== 0;

	const handleLogoutItemClick = (path: string) => {
		router.push(path);
	};

	return (
		<div className="relative" ref={dropdownRef}>
			<button onClick={() => setOpen((prev) => !prev)} className="flex cursor-pointer items-center gap-1">
				<Avatar imageUrl={profileImgObj ? getCDNURL(profileImgObj.url, 24, 24, 0, !isProPlayer) : ''} size="xs" />
				<span className="ml-1 text-sm font-medium text-white">{user.firstName}</span>
				<FontAwesomeIcon icon={faAngleDown} size="xs" className="text-white" />
			</button>

			{open ? (
				<ul className="absolute right-0 mt-2 md:mt-1 z-[150] p-2 font-normal text-sm text-gray-900 w-[180px] md:w-[212px] rounded border-none bg-white shadow-md">
					{dropdownMenuItems.map((item: DropdownUserMenuItem) => (
						<li key={`dropdown-menu-item-${item.label}-${item.id}`}>
							<UserProfileItem setOpen={setOpen} onClick={() => handleLogoutItemClick(item.src)} item={item}>
								<div className="flex items-center w-5">
									{item.label === 'My Profile' ? (
										<FontAwesomeIcon icon={faUser} size="sm" width="16" />
									) : item.label === 'Payments' ? (
										<FontAwesomeIcon icon={faCreditCard} size="sm" width="16" />
									) : item.label === 'Manage' ? (
										<FontAwesomeIcon icon={faTableColumns} size="sm" width="16" />
									) : item.label === 'Logout' ? (
										<FontAwesomeIcon icon={faSignOut} size="sm" width="16" />
									) : (
										<img src={`${getCDNURL(`${cdnUrl}${item.image}`, 18, 12)}`} alt={item.label} className="h-[11px] w-auto" />
									)}
								</div>
							</UserProfileItem>
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
};
